import React from "react";
import { Tags } from "@tintelligence/components";
import {
  OfflineIcon,
  NonFunctionalIcon,
  AttentionIcon,
} from "@tintelligence/icons";
import styled from "styled-components";
import { CloudOutlined } from "../../components/icons";
import moment from "moment-timezone";
import ReactCountryFlag from "react-country-flag";

const IconTag = styled.div`
  display: flex;
  align-items: center;
  padding: 1px;
  font: normal normal 500 12px/20px Montserrat;
  color: ${(props) => props.theme.mode.secondaryText};
  & span.anticon {
    margin-right: 8px;
  }
`;

export const statusOrder = [
  "Non-Functional",
  "Attention-Critical",
  "Attention-Warning",
  "Attention",
  "Offline",
  "Good",
];
export const getStatusTag = (status, statusMessages) => {
  switch (status) {
    case "Good": {
      return statusMessages.good;
    }
    case "Offline": {
      return (
        <Tags
          data={[
            <IconTag>
              <OfflineIcon height={16} /> {statusMessages.offline}
            </IconTag>,
          ]}
          colors={["#AA9C9C"]}
        />
      );
    }
    case "Non-Functional": {
      return (
        <Tags
          data={[
            <IconTag>
              <NonFunctionalIcon height={16} /> {statusMessages.nonFunctional}
            </IconTag>,
          ]}
          colors={["#D51E23"]}
        />
      );
    }
    case "Attention-Critical": {
      return (
        <Tags
          data={[
            <IconTag>
              <AttentionIcon height={16} /> {statusMessages.attentionCritical}
            </IconTag>,
          ]}
          colors={["#FF8900"]}
        />
      );
    }
    case "Attention-Warning": {
      return (
        <Tags
          data={[
            <IconTag>
              <AttentionIcon height={16} /> {statusMessages.attentionWarning}
            </IconTag>,
          ]}
          colors={["#FF8900"]}
        />
      );
    }
    case "Attention": {
      return (
        <Tags
          data={[
            <IconTag>
              <AttentionIcon height={16} /> {statusMessages.attention}
            </IconTag>,
          ]}
          colors={["#FF8900"]}
        />
      );
    }
    case "No Equipment": {
      return statusMessages.noEquipment;
    }
    default: {
      return "-";
    }
  }
};

export const getCustomStatusTag = (status, statusMessages) => {
  switch (status) {
    case "Good": {
      return statusMessages.good;
    }
    case "Online": {
      return (
        <Tags
          data={[
            <IconTag>
              <CloudOutlined height={16} /> {statusMessages.online}
            </IconTag>,
          ]}
        />
      );
    }
    case "Offline": {
      return (
        <Tags
          data={[
            <IconTag>
              <OfflineIcon height={16} /> {statusMessages.offline}
            </IconTag>,
          ]}
          colors={["#AA9C9C"]}
        />
      );
    }
    case "No Equipment": {
      return statusMessages.noEquipment;
    }
    default: {
      return "";
    }
  }
};
export const getStatusCountTag = (status, statusMessages) => {
  switch (status) {
    case "Good": {
      return statusMessages.good;
    }
    case "Offline": {
      return (
        <span style={{ color: "#AA9C9C" }}>
          <OfflineIcon height={16} color={"#AA9C9C"} /> {statusMessages.offline}
        </span>
      );
    }
    case "Non-Functional": {
      return (
        <span style={{ color: "#D51E23" }}>
          <NonFunctionalIcon height={16} color={"#D51E23"} />{" "}
          {statusMessages.nonFunctional}
        </span>
      );
    }
    case "Attention-Critical": {
      return (
        <span style={{ color: "#FF8900" }}>
          <AttentionIcon height={16} color={"#FF8900"} />{" "}
          {statusMessages.attentionCritical}
        </span>
      );
    }
    case "Attention-Warning": {
      return (
        <span style={{ color: "#FF8900" }}>
          <AttentionIcon height={16} color={"#FF8900"} />{" "}
          {statusMessages.attentionWarning}
        </span>
      );
    }
    case "Attention": {
      return (
        <span style={{ color: "#FF8900" }}>
          <AttentionIcon height={16} color={"#FF8900"} />{" "}
          {statusMessages.attention}
        </span>
      );
    }
    case "No Equipment": {
      return statusMessages.noEquipment;
    }
    default: {
      return "";
    }
  }
};

export const getTiers = ["core", "smart", "elite", "basic", "fleet"];

export const events = [
  { name: "Dispense", id: 1 },
  { name: "Error", id: 2 },
  { name: "Purge", id: 3 },
  { name: "Refill", id: 4 },
  { name: "DriverConfigUpdate", id: 6 },
  { name: "ServicePackUpdateEvent", id: 5 },
  { name: "Initialization", id: 7 },
  { name: "AutodriveChanges", id: 8 },
  { name: "Alert", id: 10 },
  { name: "MachineStatus", id: 11 },
  { name: "ServiceNotes", id: 12 },
];

export const getAllTimezones = () => {
  const standardDate = "2026-01-15";
  const timeZones = moment.tz.names();

  const offsetTmz = timeZones.map((tz) => ({
    key: `(UTC ${moment.tz(standardDate, tz).format("Z")}) ${tz}`,
    value: tz,
  }));

  return offsetTmz.sort((a, b) => a.key.localeCompare(b.key));
};;

let descendStatusOrder = [
  "Good",
  "Offline",
  "Attention-Warning",
  "Attention-Critical",
  "Non-Functional",
  "No Equipment",
];
let ascendStatusOrder = [
  "Non-Functional",
  "Attention-Critical",
  "Attention-Warning",
  "Offline",
  "Good",
  "No Equipment",
];

export const sortBySeverity = (a, b, sortOrder) => {
  if (a.machineStatus === b.machineStatus) {
    return 0;
  }
  if (sortOrder == "ascend") {
    return (
      ascendStatusOrder.indexOf(a.machineStatus) -
      ascendStatusOrder.indexOf(b.machineStatus)
    );
  }
  return (
    descendStatusOrder.indexOf(b.machineStatus) -
    descendStatusOrder.indexOf(a.machineStatus)
  );
};

const prodSuportedLanguage = [
  {
    countryCode: "us",
    value: "en-US", // English - United States
    displayText: "English (US)",
    displaySmallText: "English",
  },
  {
    countryCode: "cn",
    value: "zh-CN", // Chinese - simplified, Macau SAR
    displayText: "简体中文",
    displaySmallText: "Simplified Chinese",
  },
  {
    countryCode: "mo",
    value: "zh-MO", // Chinese - simplified, Macau SAR
    displayText: "繁體中文",
    displaySmallText: "Traditional Chinese",
  },
  {
    countryCode: "ro",
    value: "ro-RO", // Romanian - Romania
    displayText: "Romanian",
    displaySmallText: "Romanian",
  },
  {
    countryCode: "pl",
    value: "pl-PL", // Polish - Poland
    displayText: "Polski",
    displaySmallText: "Polish",
  },
  {
    countryCode: "es",
    value: "es-ES", // Spanish - Spain
    displayText: "Española",
    displaySmallText: "Spanish",
  },
  {
    countryCode: "fr",
    value: "fr-FR", // French - France
    displayText: "Français",
    displaySmallText: "French"
  },
  {
    countryCode: "it",
    value: "it-IT", // Italian - Italy
    displayText: "Italiano",
    displaySmallText: "Italian"
  },
  {
    countryCode: "de",
    value: "de-DE", // German - Germany
    displayText: "Deutsch",
    displaySmallText: "German"
  },
  {
    countryCode: "gr",
    value: "el-GR", // Greek - Greece
    displayText: "Ελληνικά",
    displaySmallText: "Greek"
  },
  {
    countryCode: "pt",
    value: "pt-PT", // Portuguese - Portugal
    displayText: "Português",
    displaySmallText: "Portuguese"
  },
  {
    countryCode: "jp",
    value: "ja-JP", // Japanese - Japan
    displayText: "日本語",
    displaySmallText: "Japanese"
  },
  {
    countryCode: "tr",
    value: "tr-TR", // Turkish - Turkey
    displayText: "Türkçe",
    displaySmallText: "Turkish"
  },
  // {
  //   countryCode: "nl",
  //   value: "nl-NL", // Dutch - Netherlands
  //   displayText: "Dutch",
  //   displaySmallText: "Dutch"
  // }
];

export const getLanguageOptions = () => {
  const collator = new Intl.Collator(undefined, {
    sensitivity: "base",
    ignorePunctuation: true,
  });
  const suportedLanguageOption = prodSuportedLanguage.map((lang) => ({
    label: lang.displayText,
    value: lang.value,
  }));
  return suportedLanguageOption.sort((a, b) => collator.compare(a.label, b.label));
};
export const getLabelFromOptions = (options, value) => {
  console.log("options", options, value);
  const selectedOption = options.filter((option) => {
    if (option.colorantDetailsRowId) {
      return parseInt(option.colorantDetailsRowId) === parseInt(value);
    } else if (option.colorantSystemRowId) {
      return parseInt(option.colorantSystemRowId) == parseInt(value);
    } else {
      return parseInt(option.value) === parseInt(value);
    }
  });
  console.log("selectedOption", selectedOption);
  return selectedOption
    ? selectedOption[0]?.label ||
    selectedOption[0]?.code + "-" + selectedOption[0]?.color
    : "";
};

export function toCamelCase(text) {
  return text
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase());
}

  export const TranslationConfigInfo =
   {
    LanguageTranslationContainer: "digitalsolutions-storage",
    LocalTranslationFolder: "LocalTranslation"
  }
